import { useMemo } from 'react';

import { useBestTimeSlots } from '@/hooks/analytics/useBestTimeSlots';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { getPlatformLabel } from '@/lib/platforms/identity';
import { postingTimezone, resolveSuggestedSlot } from '@/lib/scheduling/suggested-slot';

export interface SuggestedPostTime {
  at: Date;
  /** One plain sentence on what the time is based on. Never says "best" bare. */
  basis: string;
  /** True when the windows come from this workspace's own posts. */
  fromOwnHistory: boolean;
}

/**
 * The suggested time shown at the top of the schedule picker: the next free
 * slot inside the workspace's strongest posting windows (`get_best_time`),
 * skipping times already taken by other posts.
 *
 * Tier 2 is the workspace's own history; tier 0 is general platform guidance
 * and says so. Tier 1 carries no windows, and a gated result offers nothing,
 * so neither suggests a time.
 */
export function useSuggestedPostTime(
  platform: string | null | undefined,
  taken: readonly Date[],
  /** The post's own workspace; defaults to the active one. */
  brandKitId?: string | null,
): SuggestedPostTime | null {
  const best = useBestTimeSlots(platform ?? 'linkedin', brandKitId);
  const { activeKit, kits } = useBrandKits();
  const kit = (brandKitId ? kits.find((k) => k.id === brandKitId) : null) ?? activeKit;
  const { preferences } = useUserPreferences();
  const timezone = postingTimezone(kit?.timezone, preferences?.timezone);

  return useMemo(() => {
    if (!platform || best.isLoading || best.gated || best.slots.length === 0) return null;
    if (best.tier === 1) return null;
    const at = resolveSuggestedSlot(best.slots, timezone, taken, new Date());
    if (!at) return null;
    const name = getPlatformLabel(platform);
    const fromOwnHistory = best.tier === 2;
    const basis = fromOwnHistory
      ? `From ${best.n} of your ${name} posts, ranked by interactions per post · ${best.confidence} confidence`
      : `General ${name} guidance, not measured on your account yet`;
    return { at, basis, fromOwnHistory };
  }, [platform, best, timezone, taken]);
}
